import { lazy, Suspense, type ReactNode } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { LoaderCircle } from 'lucide-react'
import { AppShell } from '@/components/layout/app-shell'
import { ProtectedRoute, PublicOnlyRoute, SuperAdminRoute } from '@/components/layout/route-guards'
const AuthPage = lazy(() => import('@/pages/auth-page').then((module) => ({ default: module.AuthPage })))
const EventsPage = lazy(() => import('@/pages/events-page').then((module) => ({ default: module.EventsPage })))
const NewEventPage = lazy(() => import('@/pages/new-event-page').then((module) => ({ default: module.NewEventPage })))
const EventPage = lazy(() => import('@/pages/event-page').then((module) => ({ default: module.EventPage })))
const BillingPage = lazy(() => import('@/pages/billing-page').then((module) => ({ default: module.BillingPage })))
const AdminPage = lazy(() => import('@/pages/admin-page').then((module) => ({ default: module.AdminPage })))
const NotFoundPage = lazy(() => import('@/pages/not-found-page').then((module) => ({ default: module.NotFoundPage })))
const GuestPage = lazy(() => import('@/pages/guest-page').then((module) => ({ default: module.GuestPage })))
const UnsubscribePage = lazy(() => import('@/pages/unsubscribe-page').then((module) => ({ default: module.UnsubscribePage })))

function Deferred({ children }: { children: ReactNode }) {
  return <Suspense fallback={<div className="grid min-h-[60vh] place-items-center text-brand"><LoaderCircle className="size-7 animate-spin" /></div>}>{children}</Suspense>
}

export const router = createBrowserRouter([
  { path: '/e/:slug', element: <Deferred><GuestPage /></Deferred> },
  { path: '/unsubscribe', element: <Deferred><UnsubscribePage /></Deferred> },
  { element: <PublicOnlyRoute />, children: [
    { path: '/login', element: <Deferred><AuthPage mode="login" /></Deferred> },
    { path: '/giris', element: <Navigate to="/login" replace /> },
    { path: '/register', element: <Deferred><AuthPage mode="register" /></Deferred> },
    { path: '/kayit', element: <Navigate to="/register" replace /> },
    { path: '/forgot-password', element: <Deferred><AuthPage mode="forgot" /></Deferred> },
    { path: '/reset-password', element: <Deferred><AuthPage mode="reset" /></Deferred> },
    { path: '/sifre-sifirla', element: <Deferred><AuthPage mode="reset" /></Deferred> },
    { path: '/verify-email', element: <Deferred><AuthPage mode="verify" /></Deferred> },
    { path: '/eposta-dogrula', element: <Deferred><AuthPage mode="verify" /></Deferred> },
  ] },
  { element: <ProtectedRoute />, children: [
    { element: <AppShell />, children: [
      { path: '/', element: <Navigate to="/events" replace /> },
      { path: '/events', element: <Deferred><EventsPage /></Deferred> },
      { path: '/etkinlikler', element: <Navigate to="/events" replace /> },
      { path: '/events/new', element: <Deferred><NewEventPage /></Deferred> },
      { path: '/plans', element: <Deferred><BillingPage /></Deferred> },
      { path: '/paketler', element: <Navigate to="/plans" replace /> },
      { path: '/yeni-etkinlik', element: <Navigate to="/events/new" replace /> },
      { path: '/events/:eventId/:tab?', element: <Deferred><EventPage /></Deferred> },
      { path: '/etkinlik/:eventId/:tab?', element: <Deferred><EventPage /></Deferred> },
      { element: <SuperAdminRoute />, children: [{ path: '/admin', element: <Deferred><AdminPage /></Deferred> }, { path: '/yonetim', element: <Navigate to="/admin" replace /> }] },
    ] },
  ] },
  { path: '*', element: <Deferred><NotFoundPage /></Deferred> },
])
